import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as OpsgenieIcon } from '@elementary/common-ui/assets/opsgenie-full.svg';
import { ReactComponent as PagerDutyIcon } from '@elementary/common-ui/assets/pagerduty-full.svg';
import { Button, Flex } from '@elementary/common-ui/components';
import {
  FetchOpsgenieApi,
  FetchPagerDutyApi,
  IncidentManagementTool,
  IntegrationType,
  OpsgenieIntegrationForm,
} from 'shared/types';
import { IntegrationError } from '../../../../types';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import OpsgenieDialog from './OpsgenieDialog';
import PagerDutyDialog from './PagerDutyDialog';

const ProviderButton = styled(Button)`
  display: flex;
  height: 120px;
  padding: 24px;
  justify-content: center;
  align-items: center;
  gap: 24px;
  flex: 1 0 0;
`;

const ProviderSelection = ({
  onSelected,
}: {
  onSelected: (incidentManagementTool: IncidentManagementTool) => void;
}) => (
  <Flex gap={3}>
    <ProviderButton variant="outlined" onClick={() => onSelected('PagerDuty')}>
      <PagerDutyIcon />
    </ProviderButton>
    <ProviderButton variant="outlined" onClick={() => onSelected('Opsgenie')}>
      <OpsgenieIcon />
    </ProviderButton>
  </Flex>
);

type Props = {
  open: boolean;
  onClose: () => void;
  data?: {
    pagerduty?: FetchPagerDutyApi;
    opsgenie?: FetchOpsgenieApi;
  };
  onPagerDutyIntegrationSubmit: () => void;
  isPagerDutyIntegrationLoading?: boolean;
  pagerDutySetIntegrationError?: IntegrationError;
  onOpsgenieIntegrationSubmit: (data: OpsgenieIntegrationForm) => void;
  opsgenieSetIntegrationError?: IntegrationError;
  opsgenieLoading?: boolean;
  error?: IntegrationError;
  onDisconnect?: (integrationType: IntegrationType) => void;
};

const IncidentManagementToolDialog = ({
  open,
  onClose,
  data,
  onPagerDutyIntegrationSubmit,
  isPagerDutyIntegrationLoading,
  pagerDutySetIntegrationError,
  onOpsgenieIntegrationSubmit,
  opsgenieSetIntegrationError,
  opsgenieLoading,
  error,
  onDisconnect,
}: Props) => {
  const [userSelectedTool, setUserSelectedTool] =
    useState<IncidentManagementTool>();

  useEffect(() => {
    if (open === false) {
      setUserSelectedTool(undefined);
    }
  }, [open]);

  const selectedTool = data?.pagerduty?.integrated
    ? 'PagerDuty'
    : data?.opsgenie?.integrated
    ? 'Opsgenie'
    : userSelectedTool;

  const isEdit = data?.pagerduty?.integrated || data?.opsgenie?.integrated;

  return (
    <>
      {!selectedTool && (
        <IntegrationDialog
          open={open}
          onClose={onClose}
          title="Connect your incident management tool"
          hideSaveButton
          error={
            error || pagerDutySetIntegrationError || opsgenieSetIntegrationError
          }
        >
          <ProviderSelection
            onSelected={(value) => setUserSelectedTool(value)}
          />
        </IntegrationDialog>
      )}

      {selectedTool === 'PagerDuty' && (
        <PagerDutyDialog
          open={open}
          onClose={onClose}
          data={data?.pagerduty}
          onPagerDutyIntegrationSubmit={onPagerDutyIntegrationSubmit}
          isPagerDutyIntegrationLoading={isPagerDutyIntegrationLoading}
          error={error || pagerDutySetIntegrationError}
          edit={isEdit}
          onDisconnect={
            isEdit && onDisconnect
              ? () => onDisconnect('pager_duty')
              : undefined
          }
        />
      )}

      {selectedTool === 'Opsgenie' && (
        <OpsgenieDialog
          open={open}
          onClose={onClose}
          data={data?.opsgenie}
          onOpsgenieIntegrationSubmit={onOpsgenieIntegrationSubmit}
          error={error || opsgenieSetIntegrationError}
          loading={opsgenieLoading}
          onDisconnect={
            data?.opsgenie?.integrated && onDisconnect
              ? () => onDisconnect('opsgenie')
              : undefined
          }
        />
      )}
    </>
  );
};

export default IncidentManagementToolDialog;
